/**
 * RidesConstants
 *
 * Mirror of `backend/Libraries/CCMSLib.Models/Rides/RidesConstants.cs`.
 * Seeded lookup IDs (254a_RidesManagement_seed_pg.sql). The lookup TABLES are the source of
 * truth for names/colours/icons (see `RidesLookupsView`); these constants only name the fixed
 * IDs the engine branches on. IDs start at 1 — never 0.
 */
/** The service name API keys must list in AllowedServices. */
export const RIDES_SERVICE_NAME = 'rides-management';
export const RIDES_SEX = {
    Male: 1,
    Female: 2,
};
export const RIDES_VERIFICATION_TYPE = {
    GovernmentId: 1,
    DriversLicence: 2,
    /** The booker's parent/guardian document when the booker is (or books for) a minor. */
    GuardianId: 3,
};
export const RIDES_DOCUMENT_TYPE = {
    SouthAfricanId: 1,
    Passport: 2,
};
export const RIDES_LICENCE_TYPE = {
    SouthAfrican: 1,
    Foreign: 2,
};
/** SA licence codes allowed for a Rides driver (code B already covers vehicles <= 3 500 kg GVM). */
export const RIDES_LICENCE_CODE = {
    B: 1,
    EB: 2,
    C1: 3,
    EC1: 4,
    C: 5,
    EC: 6,
};
export const RIDES_VERIFICATION_STATUS = {
    Pending: 1,
    Verified: 2,
    Failed: 3,
    Expired: 4,
    Revoked: 5,
};
export const RIDES_AVAILABILITY_STATUS = {
    Open: 1,
    Full: 2,
    Closed: 3,
    Arrived: 4,
    Cancelled: 5,
    Completed: 6,
};
/** Listings riders may still see / book. */
export const RIDES_AVAILABILITY_STATUS_BOOKABLE = [RIDES_AVAILABILITY_STATUS.Open];
/** Listings that still represent a live ride (shown to security, cancellable). */
export const RIDES_AVAILABILITY_STATUS_LIVE = [
    RIDES_AVAILABILITY_STATUS.Open,
    RIDES_AVAILABILITY_STATUS.Full,
    RIDES_AVAILABILITY_STATUS.Arrived,
];
/** Mirror of RidesConstants.AvailabilityStatus.IsLive. */
export function isRidesAvailabilityLive(statusId) {
    return RIDES_AVAILABILITY_STATUS_LIVE.includes(statusId);
}
export const RIDES_BOOKING_STATUS = {
    Confirmed: 1,
    CancelledByRider: 2,
    CancelledByDriver: 3,
    Completed: 4,
    NoShow: 5,
};
export const RIDES_BOOKING_STATUS_ACTIVE = [RIDES_BOOKING_STATUS.Confirmed];
/** Mirror of RidesConstants.BookingStatus.IsActive. */
export function isRidesBookingActive(statusId) {
    return statusId === RIDES_BOOKING_STATUS.Confirmed;
}
export const RIDES_CONSENT_TYPE = {
    DataProcessing: 1,
    GuardianMinor: 2,
    /** 254g — the driver declaration, versioned by settings.driverDeclarationTextVersion. */
    DriverDeclaration: 3,
    /** 254g — explicit consent for special personal information (child-safety clearance upload). */
    SpecialInformation: 4,
};
export const RIDES_DISPUTE_STATUS = {
    Open: 1,
    UnderReview: 2,
    Resolved: 3,
    Dismissed: 4,
};
export const RIDES_SECURITY_CHECK_TYPE = {
    Pickup: 1,
    DropOff: 2,
};
/** Verification providers (RideSetting.VerificationProvider / RideVerification.ProviderName). */
export const RIDES_PROVIDERS = {
    Manual: 'Manual',
    Simulated: 'Simulated',
};
/** AspNetRights seeded by migration 255. */
export const RIDES_RIGHTS = {
    Read: 'Rides.Read',
    CampusesManage: 'Rides.Campuses.Manage',
    EventsManage: 'Rides.Events.Manage',
    DriversManage: 'Rides.Drivers.Manage',
    VerificationRead: 'Rides.Verification.Read',
    VerificationRecord: 'Rides.Verification.Record',
    BookingsManage: 'Rides.Bookings.Manage',
    DisputesManage: 'Rides.Disputes.Manage',
    SettingsManage: 'Rides.Settings.Manage',
    SecurityCheck: 'Rides.Security.Check',
};
export const RIDES_ROLES = {
    Admin: 'Admin',
    RidesAdmin: 'RidesAdmin',
    RidesSecurity: 'RidesSecurity',
    /** Comma list as used by the backend [CcmsAuthorize(Roles = ...)] */
    AdminRoles: 'Admin,RidesAdmin',
    SecurityRoles: 'Admin,RidesAdmin,RidesSecurity',
};
/**
 * Rule codes returned by the identity rule engine (never shown raw to members — the UI maps
 * them to plain language). Mirrored client-side by `utils/rides/identityRules`.
 */
export const RIDES_RULE_CODES = {
    Format: 'format_invalid',
    Luhn: 'luhn_failed',
    Date: 'date_invalid',
    Age: 'age_out_of_range',
    Citizenship: 'citizenship_invalid',
    RaceDigit: 'digit12_invalid',
    DobMismatch: 'dob_mismatch',
    /** Warning only: a valid SA ID disagreed with the profile date of birth, so the profile was corrected from the ID. */
    DobCorrected: 'dob_corrected',
    SexMismatch: 'sex_mismatch',
    DriverUnder18: 'driver_under_18',
    GuardianIsMinor: 'guardian_is_minor',
    Expired: 'document_expired',
    ExpiryRequired: 'expiry_required',
    CountryRequired: 'country_required',
    MrzCheckDigit: 'mrz_check_digit_failed',
    PassportPatternWarning: 'sa_passport_pattern_warning',
    LicenceCode: 'licence_code_not_allowed',
    ForeignLicenceNotUsable: 'foreign_licence_needs_english_or_idp',
    DocumentInUse: 'document_in_use',
};
/** Business error codes returned in `errors[0]` (see RidesConstants.ErrorCodes on the backend). */
export const RIDES_ERROR_CODES = {
    RegistrationClosed: 'registration_closed',
    CancellationCutoff: 'cancellation_cutoff',
    ListingCutoff: 'listing_cutoff',
    TooManyListings: 'too_many_listings',
    Maintenance: 'maintenance',
    DriverPhotoRequired: 'driver_photo_required',
    /** 409 — a Required document is missing, unreviewed (documentApprovalRequired) or expired. */
    DocumentsRequired: 'documents_required',
    /** 400 — wrong file kind, too big, or not what it claims to be. */
    DocumentInvalid: 'document_invalid',
    /** 503 — private document storage is not configured on the server. */
    DocumentStorageUnavailable: 'document_storage_unavailable',
    /** 409 — the driver declaration (current version) has not been accepted. */
    DeclarationRequired: 'declaration_required',
    /** 400/409 — fewer than minimumYearsLicensed years since the licence was first issued. */
    LicenceExperienceRequired: 'licence_experience_required',
    /** 400/409 — a Required emergency contact is missing. */
    EmergencyContactRequired: 'emergency_contact_required',
    /** 409 — under-18 booking with a driver who has no Accepted child-safety clearance. */
    DriverNotClearedForMinors: 'driver_not_cleared_for_minors',
    /** 400 — sex can't be changed after an ID check is verified or waiting (safety matching). */
    SexLocked: 'sex_locked',
    /** 409 (254i) — "I've arrived" before departure − arrivalWindowMinutes; message = `rideday.arrival_too_early` copy with the time filled. */
    ArrivalTooEarly: 'arrival_too_early',
    /** 409 (254i) — "I've arrived" after departure + arrivalGraceMinutes; message = `rideday.arrival_too_late` copy. */
    ArrivalTooLate: 'arrival_too_late',
    /** 400 (254i) — an under-18 booking without a usable childName (2–100 characters); message = `booking.child_name_required` copy. */
    ChildNameRequired: 'child_name_required',
};
/**
 * RideCopy keys the API / worker read themselves (mirror of RidesConstants.CopyKeys). Public rows arrive in
 * `settings.copy` with contact placeholders filled; `{count}`, `{documents}`, `{reason}` and (254i) `{time}` are left
 * for the portal to fill.
 */
export const RIDES_COPY_KEYS = {
    AppName: 'app.name',
    MaintenanceMessage: 'maintenance.message',
    MatchesHiddenFemaleNote: 'matches.hidden_female_note',
    MatchesEmpty: 'matches.empty',
    VerifyPendingMessage: 'verify.pending_message',
    PushBookingConfirmedRider: 'push.booking_confirmed_rider',
    PushBookingConfirmedDriver: 'push.booking_confirmed_driver',
    PushDriverArrived: 'push.driver_arrived',
    PushRideCancelled: 'push.ride_cancelled',
    PushRiderCancelled: 'push.rider_cancelled',
    PushVerificationOutcome: 'push.verification_outcome',
    SmsBookingConfirmed: 'sms.booking_confirmed',
    SmsDriverArrived: 'sms.driver_arrived',
    SmsRideCancelled: 'sms.ride_cancelled',
    // Profile photo (254f)
    PhotoIntro: 'photo.intro',
    PhotoPrivacyNote: 'photo.privacy_note',
    PhotoDriverNudge: 'photo.driver_nudge',
    PhotoDriverRequired: 'photo.driver_required',
    // Documents + safeguarding (254g)
    DocsIntro: 'docs.intro',
    DocsPrivacyNote: 'docs.privacy_note',
    DocsRequired: 'docs.required',
    DocsApprovalPending: 'docs.approval_pending',
    DocsRejected: 'docs.rejected',
    DocsClearanceConsent: 'docs.clearance_consent',
    DriverDeclarationTitle: 'driver.declaration_title',
    DriverDeclaration: 'driver.declaration',
    DriverDeclarationRequired: 'driver.declaration_required',
    LicenceExperienceRequired: 'licence.experience_required',
    EmergencyIntro: 'emergency.intro',
    EmergencyRequired: 'emergency.required',
    MatchesHiddenClearanceNote: 'matches.hidden_clearance_note',
    BookingClearanceRequired: 'booking.clearance_required',
    ProfileSexLocked: 'profile.sex_locked',
    // Ride day + under-18 bookings (254i)
    /** POPIA note on the under-18 booking step (no longer carries the group promise). */
    BookingMinorNote: 'booking.minor_note',
    /** The under-18 group rule; show it only while settings.minorRequiresGroup is true. */
    BookingMinorGroupRule: 'booking.minor_group_rule',
    BookingChildNameRequired: 'booking.child_name_required',
    /** `{time}` = when "I've arrived" opens. */
    RideDayArrivalTooEarly: 'rideday.arrival_too_early',
    RideDayArrivalTooLate: 'rideday.arrival_too_late',
};
/** RideRequirementLevel (254g): how strongly a document or field is asked for. */
export const RIDES_REQUIREMENT_LEVEL = {
    NotAsked: 1,
    Optional: 2,
    Required: 3,
};
/** RideUploadStatus (254g). */
export const RIDES_UPLOAD_STATUS = {
    Uploading: 1,
    Submitted: 2,
    Accepted: 3,
    Rejected: 4,
    Expired: 5,
    Withdrawn: 6,
};
export const RIDES_UPLOAD_STATUS_LIVE = [RIDES_UPLOAD_STATUS.Submitted, RIDES_UPLOAD_STATUS.Accepted];
/** Seeded RideUploadType IDs (254g); admin-created types start at 100. */
export const RIDES_UPLOAD_TYPE = {
    LicenceFront: 1,
    LicenceBack: 2,
    IdentityDocument: 3,
    VehicleLicenceDisc: 4,
    VehicleInsurance: 5,
    ChildSafetyClearance: 6,
    FirstCustomId: 100,
};
/** File kinds a document upload may be (checked server-side against the first bytes). */
export const RIDES_UPLOAD_CONTENT_TYPES = {
    Jpeg: 'image/jpeg',
    Png: 'image/png',
    Webp: 'image/webp',
    Pdf: 'application/pdf',
};
//# sourceMappingURL=RidesConstants.js.map